import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  completeTask,
  createTask,
  deleteTask,
  listTasks,
  updateTask,
  type CreateTaskPayload,
  type TaskFilters,
  type UpdateTaskPayload,
} from '../../../api/tasks';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

export function useTasks(filters: TaskFilters) {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: ['tasks', filters],
    queryFn: () => listTasks(filters, getToken()),
  });
}

export function useCreateTask() {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateTaskPayload) => createTask(payload, getToken()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useUpdateTask() {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      readonly id: string;
      readonly payload: UpdateTaskPayload;
    }) => updateTask(id, payload, getToken()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useCompleteTask() {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => completeTask(id, getToken()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useDeleteTask() {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteTask(id, getToken()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });
}
